import { MessageOutlined } from "@ant-design/icons";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    AssortmentTaskType,
    Market,
    SubtaskProductsMatrix,
    TaskAssortmentMatrix,
    TaskStatus,
} from "tm-shared/types";

import { BottomSheet } from "@/components/BottomSheet";
import { StatusCycleButton } from "@/components/CycleButton";
import FlexContainer from "@/components/FlexContainer";
import { OutlineIconButton } from "@/components/OutlineIconButton";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useActiveScrollNavigator } from "@/hooks/useActiveScrollNavigator";
import { useCheckCommentOnReject } from "@/hooks/useCheckCommentOnReject";
import { useStickyFilteredItems } from "@/hooks/useStickyFilteredItems";
import {
    useSubtaskConfigSelector,
    useSubtaskProductsMatrixSelector,
} from "@/hooks/useTaskSelectors";
import { patchProductsMatrixEntity } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

import { useAssortmentMatrixCompletion } from "../hooks/useAssortmentMatrixCompletion";
import { StepButton } from "../StepButton";
import { StyledBottomSheetTop } from "../Styled";
import { StyledList } from "../Styled";
import { ProductsMatrixTask } from "./components/ProductsMatrixTask";
import { StyledPMMainContainer } from "./Styled";

// Інтерфейс для пропсів компонента
interface ProductsMatrixProps {
    subtask: SubtaskProductsMatrix;
    market: Market;
    task: TaskAssortmentMatrix;
}

// Інтерфейс для інформації про товар
interface ProductInfo {
    barcode: string;
    name: string;
    finGroup?: string;
}

export const ProductsMatrix: FC<ProductsMatrixProps> = ({
    subtask,
    market,
    task,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { products } = useAppSelector((state) => state.offline);

    const marketId = market.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;
    const contractId = subtask.contractId;

    // Референс для збереження попереднього активного товару
    const previousActiveProductRef = useRef<string | null>(null);

    // Дані матриці товарів для поточної підзадачі
    const productsMatrixData = useSubtaskProductsMatrixSelector({
        taskId,
        subtaskId,
    });

    // Штрихкод активного товару
    // Флаг відображення лише незавершених товарів
    // Активна фінансова група для фільтрації
    const {
        activeItem: activeProductBarcode,
        showUncompleted: showUncompletedFilter,
        chosenFingroup: activeFinGroup,
    } = useSubtaskConfigSelector({
        marketId,
        taskId,
        subtaskId,
    });

    // Payload активного товару
    const activeProductPayload = useMemo(
        () => productsMatrixData?.products?.[activeProductBarcode] || null,
        [productsMatrixData, activeProductBarcode],
    );

    // Список штрихкодів товарів із контексту підзадачі
    const productBarcodes = useMemo(
        () => Array.from(new Set(subtask.context.products)),
        [subtask.context.products],
    );

    // Інформація про всі товари (назва, штрихкод, фінгрупа)
    const productsInfo = useMemo<ProductInfo[]>(
        () =>
            productBarcodes.map((barcode) => {
                const product = products[barcode];
                return {
                    barcode,
                    name: product?.name ?? "Невідомий товар",
                    finGroup: product?.params?.fin_group_detail?.value,
                };
            }),
        [products, productBarcodes],
    );

    // Відфільтрований набір штрихкодів, які залишаються видимими при фільтрації
    const { filteredItems: filteredProducts } = useStickyFilteredItems({
        items: productsInfo,
        showUncompleted: showUncompletedFilter ?? false,
        getItemId: (product) => product.barcode,
        payloadsMap: productsMatrixData?.products ?? {},
        additionalFilter: activeFinGroup
            ? (product) => product.finGroup === activeFinGroup
            : undefined,
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    // Назва активного товару
    const activeProductName = useMemo(() => {
        if (!activeProductBarcode) return "Невідомий товар";
        return products[activeProductBarcode]?.name ?? "Невідомий товар";
    }, [products, activeProductBarcode]);

    // Скидає статус товару "Відхилено" якщо немає коментаря
    const resetStatusIfNeeded = useCallback(
        (barcode: string) => {
            const productPayload = productsMatrixData?.products?.[barcode];

            if (!productPayload) return;
            if (
                productPayload.status === TaskStatus.Rejected &&
                !productPayload.comment
            ) {
                dispatch(
                    patchProductsMatrixEntity({
                        taskId,
                        subtaskId,
                        barcode,
                        contractId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [dispatch, taskId, subtaskId, contractId, productsMatrixData],
    );

    // Закриває bottom sheet та скидає активний товар
    const handleCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                subtaskId,
                value: null,
            }),
        );
    }, [dispatch, marketId, taskId, subtaskId]);

    // Відкриває модальне вікно для додавання коментаря до товару
    const handleOpenCommentModal = useCallback(() => {
        if (
            !activeProductBarcode ||
            activeProductPayload.status === TaskStatus.Incomplete
        )
            return;

        dispatch(
            addModalToQueue({
                type: ModalType.Comment,
                id: ModalType.Comment,
                payload: {
                    initialComment: activeProductPayload?.comment ?? "",
                    route: AssortmentTaskType.ProductsMatrix,
                    taskId,
                    subtaskId,
                    contractId,
                    barcode: activeProductBarcode,
                    taskStatus: activeProductPayload.status,
                },
            }),
        );
    }, [
        dispatch,
        activeProductPayload,
        taskId,
        subtaskId,
        contractId,
        activeProductBarcode,
    ]);

    // Відкриває модальне вікно з інформацією про задачу
    const handleOpenTaskInfoModal = useCallback(() => {
        dispatch(
            addModalToQueue({
                type: ModalType.TaskInfo,
                id: ModalType.TaskInfo,
                payload: {
                    title: subtask.name || "Матриця товарів",
                    description:
                        subtask.description ||
                        "Оберіть статус для кожного товару",
                },
            }),
        );
    }, [dispatch, subtask]);

    // Змінює активний товар
    const handleChangeActiveProduct = useCallback(
        (barcode: string) => {
            dispatch(
                setMdTasksConfig({
                    marketId,
                    taskId,
                    subtaskId,
                    value: barcode,
                }),
            );
        },
        [dispatch, marketId, taskId, subtaskId],
    );

    const { validateAndNotify } = useCheckCommentOnReject();

    // Перевіряє коментар у товарі при відхиленні
    const handleCheckCommentOnReject = useCallback(
        (barcode: string) => {
            const previousBarcode = previousActiveProductRef.current;
            const productPayload =
                productsMatrixData?.products?.[previousBarcode];

            if (validateAndNotify(productPayload)) {
                return;
            }

            handleChangeActiveProduct(barcode);
        },
        [dispatch, productsMatrixData, handleChangeActiveProduct],
    );

    // Змінює статус активного товару
    const handleChangeTaskStatus = useCallback(
        (status: TaskStatus, barcode?: string) => {
            const targetBarcode = barcode ?? activeProductBarcode;

            if (!targetBarcode) return;

            dispatch(
                patchProductsMatrixEntity({
                    taskId,
                    subtaskId,
                    contractId,
                    barcode: targetBarcode,
                    patch: { status, comment: "" },
                }),
            );
        },
        [dispatch, taskId, subtaskId, contractId, activeProductBarcode],
    );

    const navigateToProductPage = useCallback((barcode: string) => {
        navigate(`/work-plan/product-card/${barcode}`);
    }, []);

    // Скидає статус попереднього товару при зміні активного товару
    useEffect(() => {
        const previousBarcode = previousActiveProductRef.current;

        if (previousBarcode && activeProductBarcode !== previousBarcode) {
            resetStatusIfNeeded(previousBarcode);
        }

        previousActiveProductRef.current = activeProductBarcode;
    }, [activeProductBarcode, resetStatusIfNeeded]);

    // Хук для навігації по товарах з клавіатури та скролу
    const { getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredProducts.map((p) => p.barcode), // Використовуємо відфільтровані товари
            activeId: activeProductBarcode,
            onChangeActive: handleCheckCommentOnReject,
        });

    useAssortmentMatrixCompletion({
        marketId,
        taskId,
        subtaskId,
        expectedCount: productBarcodes?.length ?? 0,
        payloadsMap: productsMatrixData?.products ?? null,
    });

    useEffect(() => {
        scrollToActive();
    }, [productsMatrixData?.completed]);

    return (
        <>
            {/* Основний контейнер зі списком товарів */}
            <StyledPMMainContainer>
                <StyledList>
                    {filteredProducts.map((productInfo) => {
                        const productPayload =
                            productsMatrixData?.products?.[
                                productInfo.barcode
                            ] ?? null;
                        const isActive =
                            productInfo.barcode === activeProductBarcode;

                        return (
                            <div
                                key={productInfo.barcode}
                                ref={getItemRef(productInfo.barcode)}
                            >
                                <ProductsMatrixTask
                                    barcode={productInfo.barcode}
                                    name={productInfo.name}
                                    isActive={isActive}
                                    onChangeActive={handleChangeActiveProduct}
                                    payload={productPayload}
                                    onChangeStatus={handleChangeTaskStatus}
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <StepButton
                    task={task}
                    marketId={marketId}
                    currentCompleted={productsMatrixData?.completed}
                />
            </StyledPMMainContainer>

            {/* Bottom sheet для роботи з активним товаром */}
            <BottomSheet
                open={!!activeProductBarcode}
                onClose={handleCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={handleOpenTaskInfoModal}
            >
                <StyledBottomSheetTop>
                    <PrimaryText
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                            navigateToProductPage(activeProductBarcode)
                        }
                    >
                        {activeProductName}
                    </PrimaryText>
                    <FlexContainer gap="20px">
                        {/* Кнопка додавання коментаря */}
                        <OutlineIconButton
                            icon={<MessageOutlined />}
                            onClick={handleOpenCommentModal}
                            disabled={
                                !activeProductPayload?.status ||
                                activeProductPayload?.status ===
                                    TaskStatus.Incomplete
                            }
                        />
                        {/* Кнопка зміни статусу товару */}
                        <StatusCycleButton
                            value={
                                activeProductPayload?.status ??
                                TaskStatus.Incomplete
                            }
                            onChange={handleChangeTaskStatus}
                        />
                    </FlexContainer>
                </StyledBottomSheetTop>
            </BottomSheet>
        </>
    );
};
